import React from 'react';
import StatusGlyph from './StatusGlyph';
import './CheckRow.scss';

const KEY_ABBR = {
  http: 'HTTP', drm: 'DRM', cdn: 'CDN', url: 'URL',
  mbps: 'Mbps', ms: 'ms', avg: 'Avg', id: 'ID', m3u: 'M3U',
};

function formatKey(key) {
  return key
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (KEY_ABBR[lower]) return KEY_ABBR[lower];
      return i === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase();
    })
    .join(' ');
}

function formatValue(val) {
  if (val === true)  return 'Yes';
  if (val === false) return 'No';
  return String(val);
}

// Port / Domain field labels and display order (Figma spec)
const PORT_DOMAIN_FIELDS = [
  ['totalCount',             'Total probed'],
  ['unresolvedCount',        'Unreachable'],
  ['wildcardSkipped',        'Wildcard skipped'],
  ['deviceScopedOut',        'Device scoped out'],
  ['deviceType',             'Device type'],
  ['criticalUnreachable',    'Critical domains'],
  ['nonCriticalUnreachable', 'Non-critical domains'],
];

const CheckRow = React.forwardRef(function CheckRow({ check, focused, variant = 'result' }, ref) {
  const isAtRiskOrFail = ['fail', 'at_risk'].includes(check.status);

  // ── Loading-list variant: glyph + label only ──────────────────────────────
  if (variant === 'loading') {
    return (
      <li
        ref={ref}
        className={`RkCheckRow RkCheckRow--${check.status} RkCheckRow--loading`}
      >
        <StatusGlyph status={check.status} size={24} />
        <span className="RkCheckRow__label">{check.label}</span>
      </li>
    );
  }

  // ── Result-card variant: glyph + title + summary + detail rows ────────────
  const summary = (() => {
    if (check.id === 'portDomainReachability') {
      const { unresolvedCount, totalCount } = check.detailData || {};
      if (unresolvedCount != null && totalCount != null) {
        return `${unresolvedCount} of ${totalCount} unreachable`;
      }
    }
    return check.detail;
  })();

  const dataEntries = (() => {
    if (!check.detailData) return [];
    if (check.id === 'portDomainReachability') {
      return PORT_DOMAIN_FIELDS
        .filter(([key]) => {
          const val = check.detailData[key];
          return val != null && !(Array.isArray(val) && val.length === 0);
        })
        .map(([key, label]) => [label, check.detailData[key]]);
    }
    if (check.id === 'connectionQuality') {
      const { avgLatencyMs } = check.detailData;
      return avgLatencyMs != null ? [['Average latency', `${avgLatencyMs}ms`]] : [];
    }
    return Object.entries(check.detailData)
      .filter(([, val]) => val !== null && val !== undefined)
      .map(([key, val]) => [formatKey(key), val]);
  })();

  return (
    <li
      ref={ref}
      className={`RkCheckRow RkCheckRow--${check.status}${focused ? ' RkCheckRow--focused' : ''}`}
    >
      <div className="RkCheckRow__header">
        <StatusGlyph status={isAtRiskOrFail ? 'at_risk' : check.status} size={24} />
        <span className="RkCheckRow__title">{check.label}</span>
      </div>
      {summary && (
        <p className="RkCheckRow__summary">{summary}</p>
      )}
      {dataEntries.length > 0 && (
        <dl className="RkCheckRow__data">
          {dataEntries.map(([label, value]) => (
            <div key={label} className="RkCheckRow__data-row">
              <dt>{label}</dt>
              <dd>
                {Array.isArray(value) ? (
                  <ul className="RkCheckRow__data-list">
                    {value.map((v, i) => <li key={`${i}-${v}`}>{formatValue(v)}</li>)}
                  </ul>
                ) : (
                  formatValue(value)
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </li>
  );
});

export default CheckRow;
